if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.isBundleGallery = this.hasAttribute('data-mv-bundle-gallery');
        this.gallerySlider = this.elements.viewer?.querySelector(
          '[id^="Slider-Gallery"]'
        );

        this.counterFrame = null;

        /*
         * ================================
         * MOONVIEW — COMPTEUR UNIFIÉ
         * ================================
         *
         * Le compteur n'utilise plus les classes natives Dawn
         * `.slider-counter--current` / `.slider-counter--total`.
         *
         * Dawn ne peut donc plus réécrire le total après un swipe.
         *
         * Le numéro courant et le total sont calculés directement
         * à partir des médias réellement visibles.
         */
        if (this.gallerySlider) {
          this.gallerySlider.addEventListener(
            'scroll',
            () => {
              if (this.counterFrame) {
                cancelAnimationFrame(this.counterFrame);
              }

              this.counterFrame = requestAnimationFrame(() => {
                this.updateGalleryCounter();
              });
            },
            { passive: true }
          );

          window.addEventListener('resize', () => {
            this.updateGalleryCounter();
          });
        }

        /*
         * ================================
         * MOONVIEW — CHANGEMENT DE COULEUR
         * ================================
         */
        this.onBundleColorChange = (event) => {
          if (!this.isBundleGallery) return;

          const ownSectionId = this.id.replace(/^MediaGallery-/, '');

          if (
            event.detail?.sectionId &&
            String(event.detail.sectionId) !== String(ownSectionId)
          ) {
            return;
          }

          if (!event.detail?.color) return;

          this.filterByColor(event.detail.color);
        };

        document.addEventListener(
          'moonview:bundle-color-change',
          this.onBundleColorChange
        );

        /*
         * Corrige le compteur au chargement pour TOUS les produits.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.updateGalleryCounter();
          });
        });

        /*
         * Sur un bundle, applique la couleur active initiale.
         */
        const initialBundleColor =
          this.getAttribute('data-mv-active-color');

        if (this.isBundleGallery && initialBundleColor) {
          requestAnimationFrame(() => {
            this.filterByColor(initialBundleColor);
          });
        }

        /*
         * Dawn garde son événement slideChanged.
         * On l'utilise uniquement comme signal
         * pour resynchroniser notre compteur.
         */
        this.elements.viewer?.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 100)
        );

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');

            if (!button) return;

            button.addEventListener(
              'click',
              this.setActiveMedia.bind(
                this,
                mediaToSwitch.dataset.target,
                false
              )
            );
          });

        if (
          this.dataset.desktopLayout?.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      /*
       * ================================
       * NORMALISATION COULEUR
       * ================================
       */
      normalizeBundleColor(value) {
        const normalized = String(value || '')
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

        const aliases = {
          blanc: 'white',
          white: 'white',
          noir: 'black',
          black: 'black',
          gris: 'grey',
          gray: 'grey',
          grey: 'grey',
        };

        return aliases[normalized] || normalized;
      }

      /*
       * ================================
       * COULEUR ASSOCIÉE AU MÉDIA
       * ================================
       *
       * Compatible avec :
       *
       * color:white
       * color:white | Face
       * colour:black
       * couleur:blanc
       * couleur:noir | Dos
       */
      getBundleMediaColor(item) {
        const rawAlt = String(
          item?.dataset?.mvMediaAlt || ''
        ).trim();

        if (!rawAlt) return '';

        const direct = this.normalizeBundleColor(rawAlt);

        if (['white', 'black', 'grey'].includes(direct)) {
          return direct;
        }

        const match = rawAlt.match(
          /(?:color|colour|couleur)\s*[:=]\s*([^|;,\]]+)/i
        );

        if (!match) return '';

        return this.normalizeBundleColor(match[1]);
      }

      /*
       * ================================
       * MÉDIAS RÉELLEMENT VISIBLES
       * ================================
       */
      getVisibleGalleryItems() {
        const slider =
          this.gallerySlider ||
          this.elements.viewer?.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!slider) return [];

        return Array.from(
          slider.querySelectorAll('li[data-media-id]')
        ).filter((item) => {
          if (
            item.hidden ||
            item.style.display === 'none'
          ) {
            return false;
          }

          const style =
            window.getComputedStyle(item);

          return (
            style.display !== 'none' &&
            style.visibility !== 'hidden'
          );
        });
      }

      /*
       * ================================
       * COMPTEUR UNIFIÉ
       * ================================
       *
       * Fonctionne pour :
       *
       * - produits normaux
       * - bundles
       * - bundles filtrés par couleur
       */
      updateGalleryCounter() {
        const slider =
          this.gallerySlider ||
          this.elements.viewer?.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!slider) return;

        this.gallerySlider = slider;

        const visibleItems =
          this.getVisibleGalleryItems();

        const counterCurrent =
          this.elements.viewer?.querySelector(
            '.mv-slider-counter--current'
          );

        const counterTotal =
          this.elements.viewer?.querySelector(
            '.mv-slider-counter--total'
          );

        if (counterTotal) {
          counterTotal.textContent =
            String(visibleItems.length);
        }

        if (!visibleItems.length) {
          if (counterCurrent) {
            counterCurrent.textContent = '0';
          }

          return;
        }

        /*
         * On cherche la vraie image visible
         * la plus proche du bord gauche du slider.
         *
         * Cela ne dépend PAS du pageTotal interne de Dawn.
         */
        const sliderRect =
          slider.getBoundingClientRect();

        let closestIndex = 0;
        let closestDistance = Infinity;

        visibleItems.forEach((item, index) => {
          const itemRect =
            item.getBoundingClientRect();

          const distance = Math.abs(
            itemRect.left - sliderRect.left
          );

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (counterCurrent) {
          counterCurrent.textContent =
            String(closestIndex + 1);
        }
      }

      /*
       * ================================
       * FILTRE PAR COULEUR — BUNDLE
       * ================================
       */
      filterByColor(color) {
        if (!this.isBundleGallery) return;

        const wantedColor =
          this.normalizeBundleColor(color);

        if (!wantedColor) return;

        this.setAttribute(
          'data-mv-active-color',
          color
        );

        const viewerList =
          this.gallerySlider ||
          this.elements.viewer?.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!viewerList) return;

        this.gallerySlider = viewerList;

        const mediaItems = Array.from(
          viewerList.querySelectorAll(
            'li[data-media-id]'
          )
        );

        const taggedMatches =
          mediaItems.filter(
            (item) =>
              this.getBundleMediaColor(item) ===
              wantedColor
          );

        /*
         * Si aucun média n'est tagué avec cette couleur,
         * on affiche tout plutôt qu'une galerie vide.
         */
        const hasMatches =
          taggedMatches.length > 0;

        mediaItems.forEach((item) => {
          const shouldShow = hasMatches
            ? this.getBundleMediaColor(item) ===
              wantedColor
            : true;

          item.hidden = !shouldShow;

          item.style.display =
            shouldShow ? '' : 'none';

          item.setAttribute(
            'aria-hidden',
            shouldShow ? 'false' : 'true'
          );

          item.classList.remove('is-active');
        });

        /*
         * Même filtrage pour les miniatures.
         */
        const thumbnailItems =
          this.elements.thumbnails
            ? Array.from(
                this.elements.thumbnails.querySelectorAll(
                  'li[data-target]'
                )
              )
            : [];

        thumbnailItems.forEach((item) => {
          const shouldShow = hasMatches
            ? this.getBundleMediaColor(item) ===
              wantedColor
            : true;

          item.hidden = !shouldShow;

          item.style.display =
            shouldShow ? '' : 'none';
        });

        /*
         * Première image visible.
         */
        const firstVisible =
          mediaItems.find(
            (item) =>
              !item.hidden &&
              item.style.display !== 'none'
          );

        if (firstVisible) {
          firstVisible.classList.add(
            'is-active'
          );

          /*
           * Retour immédiat à la première image
           * de la nouvelle couleur.
           */
          viewerList.scrollTo({
            left: firstVisible.offsetLeft,
            behavior: 'auto',
          });

          const activeThumbnail =
            thumbnailItems.find(
              (item) =>
                item.dataset.target ===
                firstVisible.dataset.mediaId
            );

          if (
            activeThumbnail &&
            this.elements.thumbnails
          ) {
            this.setActiveThumbnail(
              activeThumbnail
            );
          }
        }

        /*
         * Dawn peut recalculer ses pages internes.
         * Ce n'est plus grave :
         * il ne contrôle plus notre compteur.
         */
        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();

        /*
         * On attend le recalcul du layout
         * avant de fixer définitivement le compteur.
         */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (firstVisible) {
              viewerList.scrollTo({
                left: firstVisible.offsetLeft,
                behavior: 'auto',
              });

              /*
               * MOONVIEW
               * Sur desktop uniquement :
               * on remonte la page jusqu'à la première image
               * après un changement de couleur.
               */
              if (window.matchMedia('(min-width: 990px)').matches) {
                const headerOffset =
                  parseFloat(
                    getComputedStyle(document.documentElement)
                      .getPropertyValue('--mv-header-height')
                  ) || 0;

                const targetTop =
                  firstVisible.getBoundingClientRect().top +
                  window.scrollY -
                  headerOffset -
                  18;

                window.scrollTo({
                  top: targetTop,
                  behavior: 'smooth',
                });
              }
            }

            this.updateGalleryCounter();
          });
        });
      }

      /*
       * ================================
       * CHANGEMENT DE SLIDE DAWN
       * ================================
       */
      onSlideChanged(event) {
        /*
         * Dawn change sa page interne,
         * mais notre compteur est recalculé
         * indépendamment.
         */
        requestAnimationFrame(() => {
          this.updateGalleryCounter();
        });

        if (!this.elements.thumbnails) return;

        const currentElement =
          event.detail?.currentElement;

        if (!currentElement) return;

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${currentElement.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      /*
       * ================================
       * MÉDIA ACTIF
       * ================================
       */
      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer?.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer?.querySelector(
            '[data-media-id]:not([hidden])'
          ) ||
          this.elements.viewer?.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) return;

        this.elements.viewer
          ?.querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove(
              'is-active'
            );
          });

        activeMedia.classList.add(
          'is-active'
        );

        if (prepend) {
          if (
            activeMedia.parentElement &&
            activeMedia.parentElement.firstChild !==
              activeMedia
          ) {
            activeMedia.parentElement.prepend(
              activeMedia
            );
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement &&
              activeThumbnail.parentElement.firstChild !==
                activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }

          this.elements.viewer?.resetPages?.();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          const parent =
            activeMedia.parentElement;

          if (
            parent &&
            (!this.mql.matches ||
              this.elements.thumbnails)
          ) {
            parent.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          /*
           * Pas de scroll vertical
           * si le média est déjà visible.
           */
          if (activeMediaRect.top > -0.5) {
            return;
          }

          const top =
            activeMediaRect.top +
            window.scrollY;

          window.scrollTo({
            top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        requestAnimationFrame(() => {
          this.updateGalleryCounter();
        });

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(
          activeThumbnail
        );

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      /*
       * ================================
       * MINIATURE ACTIVE
       * ================================
       */
      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute(
              'aria-current'
            );
          });

        const button =
          thumbnail.querySelector('button');

        if (!button) return;

        button.setAttribute(
          'aria-current',
          'true'
        );

        if (
          this.elements.thumbnails.isSlideVisible?.(
            thumbnail,
            10
          )
        ) {
          return;
        }

        if (
          this.elements.thumbnails.slider
        ) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
          });
        }
      }

      /*
       * ================================
       * ACCESSIBILITÉ
       * ================================
       */
      announceLiveRegion(
        activeItem,
        position
      ) {
        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          if (!this.elements.liveRegion) {
            return;
          }

          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            'false'
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings
              .imageAvailable
              .replace(
                '[index]',
                position
              );

          setTimeout(() => {
            this.elements.liveRegion?.setAttribute(
              'aria-hidden',
              'true'
            );
          }, 2000);
        };

        image.src = image.src;
      }

      /*
       * ================================
       * MÉDIAS VIDÉO / 3D
       * ================================
       */
      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      /*
       * ================================
       * HEADER STICKY
       * ================================
       */
      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event(
            'preventHeaderReveal'
          )
        );
      }

      /*
       * ================================
       * ACCESSIBILITÉ LISTE DESKTOP
       * ================================
       */
      removeListSemantic() {
        if (!this.elements.viewer?.slider) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems?.forEach(
          (slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          }
        );
      }
    }
  );
}